import { useState } from "react"

import { useTranslation } from "react-i18next"

import type { AlertLogEntry } from "@/api/alerts-api"

import { AlertCircle, AlertTriangle, Check, Info, RefreshCw, X } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

import { useAdminAlertLogsQuery } from "../../hooks/admin-queries"

function SeverityBadge({ severity }: { severity: string }) {
  switch (severity) {
    case "critical":
      return (
        <Badge variant="destructive">
          <AlertCircle className="mr-1 size-3" />
          Critical
        </Badge>
      )
    case "warning":
      return (
        <Badge variant="warning">
          <AlertTriangle className="mr-1 size-3" />
          Warning
        </Badge>
      )
    case "info":
    default:
      return (
        <Badge variant="secondary">
          <Info className="mr-1 size-3" />
          Info
        </Badge>
      )
  }
}

function formatDate(isoString: string): string {
  const date = new Date(isoString)
  return date.toLocaleString()
}

export function AlertsLogTable() {
  const { t } = useTranslation()
  const [filters, setFilters] = useState<{
    alert_type?: string
    severity?: string
    limit: number
  }>({
    limit: 50,
  })

  const { data, isLoading, refetch } = useAdminAlertLogsQuery(filters)

  const handleFilterChange = (key: string, value: string | undefined) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value === "all" ? undefined : value,
    }))
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <Select
          value={filters.severity || "all"}
          onValueChange={(value) => handleFilterChange("severity", value)}
        >
          <SelectTrigger aria-label="Severity" className="w-40">
            <SelectValue placeholder={t("adminDashboard.alerts.filterBySeverity", "Severity")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("common.all", "All")}</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
            <SelectItem value="warning">Warning</SelectItem>
            <SelectItem value="info">Info</SelectItem>
          </SelectContent>
        </Select>

        <Input
          aria-label="Alert type"
          className="w-48"
          placeholder={t("adminDashboard.alerts.filterByType", "Alert type...")}
          value={filters.alert_type || ""}
          onChange={(e) => handleFilterChange("alert_type", e.target.value || undefined)}
        />

        <Button size="sm" variant="outline" onClick={() => refetch()}>
          <RefreshCw className="mr-2 size-4" />
          {t("common.refresh", "Refresh")}
        </Button>
      </div>

      {/* Table */}
      <div className="rounded-lg border">
        <div className="bg-muted/50 hidden grid-cols-12 gap-4 border-b px-4 py-2 text-sm font-medium lg:grid">
          <div className="col-span-2">{t("adminDashboard.alerts.time", "Time")}</div>
          <div className="col-span-1">{t("adminDashboard.alerts.severity", "Severity")}</div>
          <div className="col-span-2">{t("adminDashboard.alerts.type", "Type")}</div>
          <div className="col-span-4">{t("adminDashboard.alerts.subject", "Subject")}</div>
          <div className="col-span-2">{t("adminDashboard.alerts.channels", "Channels")}</div>
          <div className="col-span-1">{t("adminDashboard.alerts.status", "Status")}</div>
        </div>

        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="text-muted-foreground size-6 animate-spin" />
          </div>
        )}
        {!isLoading && (!data || data.logs.length === 0) && (
          <div className="text-muted-foreground px-4 py-8 text-center">
            {t("adminDashboard.alerts.noLogs", "No alerts found")}
          </div>
        )}
        {!isLoading &&
          data &&
          data.logs.length > 0 &&
          data.logs.map((log: AlertLogEntry) => (
            <div
              key={log.id}
              className="admin-responsive-grid-row hover:bg-muted/50 grid grid-cols-12 items-center gap-4 border-b px-4 py-3 text-sm last:border-b-0"
            >
              <div className="text-muted-foreground col-span-2 flex items-center gap-3 lg:block">
                <span className="text-foreground w-20 shrink-0 text-xs font-medium lg:hidden">
                  Time
                </span>
                <span>{formatDate(log.created)}</span>
              </div>
              <div className="col-span-1 flex items-center gap-3 lg:block">
                <span className="text-muted-foreground w-20 shrink-0 text-xs font-medium lg:hidden">
                  Severity
                </span>
                <SeverityBadge severity={log.severity} />
              </div>
              <div className="col-span-2 flex items-center gap-3 lg:block">
                <span className="text-muted-foreground w-20 shrink-0 text-xs font-medium lg:hidden">
                  Type
                </span>
                <code className="bg-muted rounded px-1.5 py-0.5 text-xs">{log.alert_type}</code>
              </div>
              <div
                className="col-span-4 flex min-w-0 items-center gap-3 lg:block lg:truncate"
                title={log.subject}
              >
                <span className="text-muted-foreground w-20 shrink-0 text-xs font-medium lg:hidden">
                  Subject
                </span>
                <span className="truncate">{log.subject}</span>
              </div>
              <div className="col-span-2 flex flex-wrap gap-1">
                <span className="text-muted-foreground mr-auto w-20 shrink-0 text-xs font-medium lg:hidden">
                  Channels
                </span>
                {log.channels_sent.map((ch) => (
                  <Badge key={ch} className="text-xs" variant="outline">
                    <Check className="text-success mr-1 size-3" />
                    {ch}
                  </Badge>
                ))}
                {log.channels_failed.map((ch) => (
                  <Badge key={ch} className="text-xs" variant="outline">
                    <X className="text-destructive mr-1 size-3" />
                    {ch}
                  </Badge>
                ))}
              </div>
              <div className="col-span-1">
                <span className="text-muted-foreground mr-3 text-xs font-medium lg:hidden">
                  Status
                </span>
                {log.delivery_mode === "sent" && (
                  <Badge variant="success">
                    {t("adminDashboard.alerts.deliveryModeSent", "Sent")}
                  </Badge>
                )}
                {log.delivery_mode === "dry_run" && (
                  <Badge variant="secondary">
                    {t("adminDashboard.alerts.deliveryModeDryRun", "Dry Run")}
                  </Badge>
                )}
                {log.delivery_mode !== "sent" && log.delivery_mode !== "dry_run" && (
                  <Badge variant="outline">
                    {t("adminDashboard.alerts.deliveryModeLogOnly", "Log Only")}
                  </Badge>
                )}
              </div>
            </div>
          ))}
      </div>

      {data && data.count > 0 && (
        <div className="text-muted-foreground text-sm">
          {t("adminDashboard.alerts.showing", "Showing {{count}} alerts", {
            count: data.count,
          })}
        </div>
      )}
    </div>
  )
}
